<template>
  <div :class="embedded ? '' : 'p-4 bg-white shadow rounded mb-6'">
    <div class="relative flex flex-wrap items-center gap-3">
      <button
        type="button"
        class="min-w-[230px] rounded border border-gray-300 bg-white px-3 py-1.5 text-left"
        @click="isCalendarOpen = !isCalendarOpen"
      >
        {{ rangeLabel }}
      </button>

      <button
        class="bg-[#0dabcb] hover:bg-[#3c9ca4] text-white px-4 py-1.5 rounded"
        @click="apply"
      >
        Apply
      </button>

      <div
        v-if="isCalendarOpen"
        class="insights-calendar-popover rounded border border-gray-200 bg-white p-3 shadow-lg"
      >
        <DatePicker
          v-model="calendarRange"
          is-range
          mode="date"
          :attributes="attrs"
        />
        <p class="mt-2 max-w-xs text-xs text-gray-500">
          Select any date in the week. The full Monday to Sunday range will be selected.
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted } from 'vue'
import { useEmployeeInsightsStore } from '@/pinia/employeeInsightsStore'
import 'v-calendar/dist/style.css'
import { DatePicker } from 'v-calendar'

defineProps({
  embedded: {
    type: Boolean,
    default: false
  }
})

const today = new Date()
const isCalendarOpen = ref(false)
const skipNextCalendarWatch = ref(false)
const calendarRange = ref({ start: today, end: today })
const pendingRange = ref({ start: today, end: today })

const store = useEmployeeInsightsStore()

const attrs = computed(() => [
  {
    key: 'selectedRange',
    highlight: {
      start: { fillMode: 'solid', color: 'gray' },
      base: { fillMode: 'light', color: 'gray' },
      end: { fillMode: 'solid', color: 'gray' }
    },
    dates: pendingRange.value
  }
])

const rangeLabel = computed(() => {
  const { start, end } = pendingRange.value
  if (!start || !end) return 'Select week'
  return `${formatDisplayDate(start)} - ${formatDisplayDate(end)}`
})

function startOfWeek(date) {
  const start = new Date(date)
  const day = start.getDay() || 7
  start.setDate(start.getDate() - day + 1)
  return start
}

function buildWeekRange(selection) {
  const selectedDate = selection?.start || selection
  if (!selectedDate) return pendingRange.value

  const start = startOfWeek(new Date(selectedDate))
  return {
    start,
    end: new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6)
  }
}

function setPendingRange(range) {
  pendingRange.value = range
  skipNextCalendarWatch.value = true
  calendarRange.value = range
}

function formatDisplayDate(date) {
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(date)
}

function localCalendarDateFromEpoch(epoch) {
  const date = new Date(epoch * 1000)
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
}

function syncFromStoreRange() {
  if (!store.startDate || !store.endDate) return false
  const start = localCalendarDateFromEpoch(store.startDate)
  setPendingRange(buildWeekRange(start))
  return true
}

function applyCurrentWeek() {
  setPendingRange(buildWeekRange(new Date()))
  apply()
}

function apply() {
  const { start, end } = pendingRange.value
  if (!start || !end) return

  const startEpoch = Math.floor(Date.UTC(
    start.getFullYear(),
    start.getMonth(),
    start.getDate()
  ) / 1000)

  const endEpoch = Math.floor(Date.UTC(
    end.getFullYear(),
    end.getMonth(),
    end.getDate(),
    23, 59, 59
  ) / 1000)

  store.updateDateRange(startEpoch, endEpoch)
  isCalendarOpen.value = false
}

onMounted(() => {
  applyCurrentWeek()
})

watch(calendarRange, (newRange) => {
  if (skipNextCalendarWatch.value) {
    skipNextCalendarWatch.value = false
    return
  }
  if (!newRange) return
  setPendingRange(buildWeekRange(newRange))
})

watch(
  () => [store.startDate, store.endDate],
  () => {
    syncFromStoreRange()
  }
)
</script>

<style scoped>
.insights-calendar-popover {
  position: absolute;
  left: 0;
  top: calc(100% + 0.75rem);
  z-index: 60;
}
</style>
